/**
 * Locale identifiers for the tenant console-web surface.
 *
 * We ship two locales — `zh-CN` (default, tenant-facing audience is
 * predominantly Chinese-speaking) and `en` (English for international
 * developers and internal QA). The CLAUDE.md global rule spells out the
 * dictionary layout (`src/i18n/{zh-CN,en,index}.ts`) and the `useT()`
 * hook signature; the types here sit upstream of both files.
 *
 * Adding a locale is an intentional, breaking change — the dictionary
 * type guarantee (`typeof zhCN` in `en.ts`) means every key must exist
 * in every locale, so a new locale forces authoring every string. That
 * is on purpose: partial translations are worse than a single-language
 * fallback.
 */
export type Locale = 'zh-CN' | 'en';

/**
 * Runtime list of supported locales, exposed so the switcher can
 * enumerate without re-declaring the set. Kept in display order (zh-CN
 * first because it is the default). Typed `readonly Locale[]` so any
 * `.map` / `.forEach` downstream gets the narrow element type.
 */
export const SUPPORTED_LOCALES: readonly Locale[] = ['zh-CN', 'en'];

/**
 * Fallback locale when no localStorage preference is present and no
 * `initialLocale` prop is forced by the test rig. Chinese-by-default
 * matches the product positioning; English users flip the switcher and
 * the choice persists.
 */
export const DEFAULT_LOCALE: Locale = 'zh-CN';

/**
 * Native-script label for each locale — the name of each language in
 * its OWN language, not the currently-active one. Rendered by the
 * `LocaleSwitcher` so a user who cannot read the active locale can
 * still find their preferred language ("中文" stays 中文 even when the
 * UI is in English). These are UI chrome, not translations: they never
 * change with `setLocale`, so keeping them in a dictionary would be
 * actively misleading.
 */
export const LOCALE_NATIVE_LABELS: Readonly<Record<Locale, string>> = {
  'zh-CN': '中文',
  en: 'English',
};

/**
 * Shape of template parameters accepted by `t()`. Templates reference
 * params via `{name}` — see `format.ts` for the substitution rules.
 * Values coerce through `String()` so both numbers and strings work;
 * callers that need richer formatting (e.g. currency) should pre-format
 * upstream and pass the display string in.
 */
export type TParams = Readonly<Record<string, string | number>>;

/**
 * Local type guard for `Locale`. Used by the provider to validate a
 * `localStorage` read without an `as`-cast. Kept as an explicit union
 * check (rather than `SUPPORTED_LOCALES.includes(value)`) so TypeScript
 * narrows correctly without widening `SUPPORTED_LOCALES` to
 * `readonly string[]`. When a new locale is added to the `Locale`
 * union above, TypeScript reports an exhaustiveness error here via
 * the `assertNever` default branch, which forces the author to extend
 * the guard in lockstep.
 */
export function isLocale(value: unknown): value is Locale {
  if (typeof value !== 'string') {
    return false;
  }
  switch (value) {
    case 'zh-CN':
    case 'en':
      return true;
    default:
      return false;
  }
}
